import React, { useEffect, useState } from "react";
import moment from "moment";
import { portalService } from "apiServices";
import { IMG_PATH } from "utils/constants/imgPath";
import { RenderCommaMoney } from "utils/functions/FormatMoney";
import { Buttons, Cards, Displays } from "components";
import {
  BOOKING_STATUS,
  CREATED_BY,
  RENDER_BOOKING_SOURCE,
  ADMIN_BOOKING_WARNINGS,
  RENDER_SCHOOL,
  RENDER_SEAT_BOOKING_ACTION,
  RENDER_SEAT_BOOKING_STATUS,
} from "utils/functions/seatBooking";

const SLIP_CHECK_LABEL = { 1: "ผ่าน", 2: "มีคำเตือน", 3: "ยังไม่ได้ตรวจอัตโนมัติ" };
const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

const BookingDetailModal = ({ bookingId, onClose }) => {
  const [_data, _setData] = useState();

  useEffect(() => {
    const fetchData = async () => {
      const res = await portalService.GET_SEAT_BOOKING_DETAIL(bookingId);
      if (res && res.status === 200) _setData(res.data);
    };
    fetchData();
  }, [bookingId]);

  if (!_data) {
    return (
      <div className="modal_card">
        <Displays.Loading theme_standard_loading heightLayout="50vh" />
      </div>
    );
  }

  const status = RENDER_SEAT_BOOKING_STATUS(_data.status);
  const booker = _data.booker || {};
  const tax = _data.tax_invoice || {};

  return (
    <div className="modal_card wide">
      <div className="modal_title">รายละเอียดการจอง {_data.code}</div>
      <div className="detail_grid">
        <div className="d_label">สถานะ</div>
        <div>
          <Displays.StatusLabel bg={status.bgColor} txtColor={status.txtColor} label={status.labelTh} />
        </div>
        <div className="d_label">ประเภท</div>
        <div>
          {RENDER_BOOKING_SOURCE(_data.booking_source)} ·{" "}
          {_data.created_by_type === CREATED_BY.ADMIN
            ? `แอดมินจองให้ (${_data.created_by_admin_name || "-"})`
            : "ลูกค้าจองเอง"}
        </div>
        <div className="d_label">ผู้จอง</div>
        <div>
          {booker.first_name} {booker.last_name} · {booker.phone || "-"} · {booker.email || "-"}
        </div>
        <div className="d_label">โรงเรียน</div>
        <div>
          {RENDER_SCHOOL(_data.school_code, _data.school_name)}
        </div>
        <div className="d_label">ยอด</div>
        <div>{RenderCommaMoney(_data.amount)} บาท</div>
        <div className="d_label">ส่งเมื่อ</div>
        <div>{formatDate(_data.submitted_at)}</div>
        {_data.decided_at && (
          <>
            <div className="d_label">ตัดสินโดย</div>
            <div>
              {_data.decided_by_admin_name || "-"} · {formatDate(_data.decided_at)}
            </div>
          </>
        )}
        {_data.reject_reason && (
          <>
            <div className="d_label">เหตุผลที่ปฏิเสธ</div>
            <div>{_data.reject_reason}</div>
          </>
        )}
        {_data.cancel_reason && (
          <>
            <div className="d_label">เหตุผลที่ยกเลิก</div>
            <div>{_data.cancel_reason}</div>
          </>
        )}
        {_data.note && (
          <>
            <div className="d_label">หมายเหตุ</div>
            <div>{_data.note}</div>
          </>
        )}
        <div className="d_label">ใบกำกับภาษี</div>
        <div>
          {tax.is_required
            ? `${tax.name || ""} ${tax.branch || ""} · เลขผู้เสียภาษี ${tax.tax_id || "-"} · ${tax.address || ""}`
            : "ไม่ต้องการ"}
        </div>
      </div>

      {ADMIN_BOOKING_WARNINGS(_data).length > 0 && (
        <div className="warn_box">
          ต้องเช็ค:
          <ul>
            {ADMIN_BOOKING_WARNINGS(_data).map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="section_title">สลิป</div>
      {(_data.payments || []).length === 0 ? (
        <div className="hint">ไม่มีสลิป</div>
      ) : (
        _data.payments.map((p, i) => (
          <div className="inline_row" key={p._id || i}>
            <a href={`${IMG_PATH}${p.slip_path}`} target="_blank" rel="noopener noreferrer">
              Link{_data.payments.length > 1 ? ` ${i + 1}` : ""}
            </a>
            <span>
              ยอดในสลิป {p.transfer_amount !== null && p.transfer_amount !== undefined ? RenderCommaMoney(p.transfer_amount) : "-"} ·
              โอนเมื่อ {formatDate(p.paid_at)} · ตรวจ: {SLIP_CHECK_LABEL[p.slip_check_status] || "-"}
              {p.trans_ref ? ` · ref ${p.trans_ref}` : ""}
            </span>
          </div>
        ))
      )}

      <div className="section_title">ที่นั่ง / ตั๋ว</div>
      <div className="qr_row">
        {_data.seats.map((s) => (
          <div className="qr_item" key={s.seat_id}>
            {/* ตั๋วของแขกที่ไม่ผูกสมาชิก แอดมินดาวน์โหลด QR ส่งให้เองได้ */}
            {_data.status === BOOKING_STATUS.APPROVED && s.ticket_qr ? (
              <Cards.QrCodeItem code={s.ticket_qr} label={`${_data.code}-${s.seat_id}`} />
            ) : null}
            <div>
              <b>{s.seat_id}</b> {s.viewer_name ? `· ${s.viewer_name}` : ""}
            </div>
            <div className="hint">
              {s.used_at ? `เข้างานแล้ว ${formatDate(s.used_at)} (${s.used_by_admin_name || "-"})` : "ยังไม่เข้างาน"}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <Displays.ActionTimeline
          items={(_data.logs || []).map((l) => {
            const action = RENDER_SEAT_BOOKING_ACTION(l.action_type);
            const by = l.actor_type === "admin" ? `โดยแอดมิน ${l.actor_name || "-"}` : l.actor_type === "customer" ? "โดยลูกค้า" : "โดยระบบ";
            return {
              key: l._id,
              label: `${action.label} · ${by}`,
              color: action.color,
              created_at: l.created_at,
              note: l.note,
            };
          })}
        />
      </div>

      <div className="modal_btns">
        <div className="w120">
          <Buttons.BgStandard theme_gray label="ปิด" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default BookingDetailModal;
